import { inject, Injectable } from '@angular/core';
import { ISideListOptionsItem } from '../../fi-items-list';
import {
    IItemsListFilter,
    ISelectedFilterItem,
    IItemsListFilterSettings,
} from '../fi-items-list-filter/fi-items-list-filter';
import { FiFilterType } from '../fi-filter-type-enum';
import { SideListOptionType } from '@fi-shared/types';
import { ILightWeightArea, ILightWeightBusinessUnit, ILightWeightObject, ILightWeightUser } from '@fi-shared/models';
import { isDefined } from '@fi-app/shared/utils';
import { DateRange } from '@angular/material/datepicker';
import { FiItemsListFilterHelperService } from '../fi-items-list-filter-helper.service';
import { IItemsListFiltersData } from '../fi-items-list-filter/fi-items-list-filter';
import { IAccessRightFilterCollection } from '@fi-shared/models';

export interface IGetSelectedFilterItemsArgs {
    currentFilterSettings: IItemsListFilterSettings;
    tagItems: ISideListOptionsItem[];
    categoryItems: ISideListOptionsItem[];
    accessRightFilterCollection: IAccessRightFilterCollection;
    changedItems: ISideListOptionsItem[];
}

@Injectable()
export class ItemsListFilterSelectionMapperService {
    private itemsListFilterHelperService = inject(FiItemsListFilterHelperService);

    private convertSideListOptionsItemToFilterItem(
        item: ISideListOptionsItem,
        filterType: FiFilterType,
    ): ISelectedFilterItem {
        if (item) {
            return {
                name: item.text,
                value: item.value as number,
                filterType,
            };
        }
    }

    private getTagsSelectedFilterItems(
        selectedTagsIds: number[],
        tagItems: ISideListOptionsItem[],
    ): ISelectedFilterItem[] {
        if (!selectedTagsIds.length || !tagItems.length) {
            return [];
        }
        return selectedTagsIds.map((tagId) => {
            const selectedTag = tagItems.find((tag) => {
                return tag.value === tagId;
            });
            return this.convertSideListOptionsItemToFilterItem(selectedTag, FiFilterType.Tag);
        });
    }

    private getCategorySelectedFilterItems(
        selectedCategoryTypes: SideListOptionType[],
        categoryItems: ISideListOptionsItem[],
    ): ISelectedFilterItem[] {
        if (!selectedCategoryTypes.length || !categoryItems.length) {
            return [];
        }
        return selectedCategoryTypes.map((category: SideListOptionType) => {
            const selectedCategory = categoryItems.find((categoryItem) => categoryItem.value === category);
            return this.convertSideListOptionsItemToFilterItem(selectedCategory, FiFilterType.Category);
        });
    }

    private getUsersSelectedFilterItems(selectedUserIds: number[], users: ILightWeightUser[]): ISelectedFilterItem[] {
        if (!selectedUserIds.length || !users.length) {
            return [];
        }
        return selectedUserIds.map((userId: number) => {
            const selectedUser = users.find((categoryItem: ILightWeightUser) => categoryItem.Id === userId);
            return this.convertSideListOptionsItemToFilterItem(
                {
                    text: selectedUser.FullName,
                    value: selectedUser.Id,
                },
                FiFilterType.User,
            );
        });
    }

    private getBusinessUnitsSelectedFilterItems(
        selectedBusinessUnitIds: number[],
        businessUnits: ILightWeightBusinessUnit[],
    ): ISelectedFilterItem[] {
        if (!selectedBusinessUnitIds.length || !businessUnits.length) {
            return [];
        }
        return selectedBusinessUnitIds.map((businessUnitId: number) => {
            const selectedBusinessUnit = businessUnits.find(
                (businessUnit: ILightWeightBusinessUnit) => businessUnit.Id === businessUnitId,
            );
            return this.convertSideListOptionsItemToFilterItem(
                {
                    text: selectedBusinessUnit.Name,
                    value: selectedBusinessUnit.Id,
                },
                FiFilterType.BusinessUnit,
            );
        });
    }

    private getAreaSelectedFilterItems(selectedAreaIds: number[], areas: ILightWeightArea[]): ISelectedFilterItem[] {
        if (!selectedAreaIds.length || !areas.length) {
            return [];
        }
        return selectedAreaIds.map((areaId: number) => {
            const selectedArea = areas.find((area: ILightWeightArea) => area.Id === areaId);
            return this.convertSideListOptionsItemToFilterItem(
                {
                    text: selectedArea.Name,
                    value: selectedArea.Id,
                },
                FiFilterType.Area,
            );
        });
    }

    private getClusterSelectedFilterItems(
        selectedClusterIds: number[],
        clusters: ILightWeightObject[],
    ): ISelectedFilterItem[] {
        if (!selectedClusterIds.length || !clusters.length) {
            return [];
        }
        return selectedClusterIds.map((clusterId: number) => {
            const selectedCluster = clusters.find((cluster: ILightWeightObject) => cluster.Id === clusterId);
            return this.convertSideListOptionsItemToFilterItem(
                {
                    text: selectedCluster.Name,
                    value: selectedCluster.Id,
                },
                FiFilterType.Cluster,
            );
        });
    }

    private getBusinessAreaSelectedFilterItems(
        selectedBusinessAreaIds: number[],
        businessAreas: ILightWeightObject[],
    ): ISelectedFilterItem[] {
        if (!selectedBusinessAreaIds.length || !businessAreas.length) {
            return [];
        }
        return selectedBusinessAreaIds.map((businessAreaId: number) => {
            const selectedBusinessArea = businessAreas.find(
                (businessArea: ILightWeightObject) => businessArea.Id === businessAreaId,
            );
            return this.convertSideListOptionsItemToFilterItem(
                {
                    text: selectedBusinessArea.Name,
                    value: selectedBusinessArea.Id,
                },
                FiFilterType.BusinessArea,
            );
        });
    }

    private getChangedSelectedFilterItems(
        selectedChangedOnFilterType: number,
        selectedChangedItem: ISideListOptionsItem,
    ): ISelectedFilterItem[] {
        if (!isDefined(selectedChangedOnFilterType)) {
            return [];
        }
        return [this.convertSideListOptionsItemToFilterItem(selectedChangedItem, FiFilterType.LastEdit)];
    }

    private getChangedDateRangeSelectedFilterItem(changedOnRange: DateRange<Date>): ISelectedFilterItem[] {
        if (!isDefined(changedOnRange)) {
            return [];
        }
        return [
            {
                name: this.itemsListFilterHelperService.getCustomDateFilterLabel(
                    changedOnRange.start,
                    changedOnRange.end,
                ),
                value: changedOnRange,
                filterType: FiFilterType.CustomDate,
            },
        ];
    }

    getSelectedFilterItems(args: IGetSelectedFilterItemsArgs): ISelectedFilterItem[] {
        const acrCoreFilter = args.accessRightFilterCollection.AccessRightCoreFilter;
        const acrUserFilter = args.accessRightFilterCollection.AccessRightUserFilter;
        return [
            ...this.getTagsSelectedFilterItems(args.currentFilterSettings.TagIds, args.tagItems),
            ...this.getCategorySelectedFilterItems(args.currentFilterSettings.CategoryFilterTypes, args.categoryItems),
            ...this.getUsersSelectedFilterItems(args.currentFilterSettings.UserIds, acrUserFilter.Users),
            ...this.getBusinessUnitsSelectedFilterItems(
                args.currentFilterSettings.BusinessUnitIds,
                acrCoreFilter.BusinessUnits,
            ),
            ...this.getAreaSelectedFilterItems(args.currentFilterSettings.AreaIds, acrCoreFilter.Areas),
            ...this.getClusterSelectedFilterItems(args.currentFilterSettings.ClusterIds, acrCoreFilter.Clusters),
            ...this.getBusinessAreaSelectedFilterItems(
                args.currentFilterSettings.BusinessAreaIds,
                acrCoreFilter.BusinessAreas,
            ),
            ...this.getChangedSelectedFilterItems(
                args.currentFilterSettings.ChangedOnFilterType,
                args.changedItems.find((changedItem: ISideListOptionsItem) => {
                    return changedItem.value === args.currentFilterSettings.ChangedOnFilterType;
                }),
            ),
            ...this.getChangedDateRangeSelectedFilterItem(args.currentFilterSettings.ChangedOnRange),
        ];
    }
}
